import { Component, computed, effect, OnInit, Signal, ViewChild, linkedSignal, WritableSignal, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { Observable, Subscription } from 'rxjs';
import { ErrorWarningDialogComponent } from '../error-warning-dialog/error-warning-dialog.component';
import { AuthService } from '../shared/auth.service';
import { FishingEvent } from '../shared/fishing-event.model';
import { Fishery, LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';
import { SetErrorBlock } from '../shared/set-error-block.model';
import { lookupItemValidator, TripValidator } from '../shared/trip-validator.model';
import { FormControl, FormGroup } from '@angular/forms';
import { LookupItem } from '../shared/lookup-item.model';
import { map, startWith } from 'rxjs/operators';
import { LookupDataService } from '../shared/lookup-data.service';
import { formatDate } from '@angular/common';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { TrawlSet } from '../shared/trawl-set.model';
import { ToastrService } from 'ngx-toastr';
import { Insights } from '../shared/insights.service';
import { Gear } from '../shared/gear.model';
import { Coordinate } from '../shared/coordinate.model';
import { HmsInportDialogComponent, HmsInPortResult } from '../hms-inport-dialog/hms-inport-dialog.component';



/**
 * The main menu, which displays a table view of the set list
 * From here we can go to the, completed delivery list, current delivery, the settings, or the profile page.
 * All these are their own components
 */
@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.css'],
    standalone: false
})
export class MenuComponent implements OnInit {

  editedDeliveryId: Signal<string>;
  editedTrawlDeliveryId: Signal<string>;
  hasDraftTrips: Signal<boolean>;

  @ViewChild('currentDeliveryMenuTrigger') currentDeliveryMenuTrigger!: MatMenuTrigger;
  @ViewChild('addSetMenuTrigger') addSetMenuTrigger!: MatMenuTrigger;
  panelOpen: WritableSignal<boolean>;

  setErrors: Signal<SetErrorBlock[]>;

  groundfishSets: Signal<FishingEvent[]>;
  hmsSets: Signal<FishingEvent[]>;
  trawlSets: Signal<TrawlSet[]>;
  selectedFishery: WritableSignal<Fishery>;

  landingForm: FormGroup = new FormGroup({
    departureDate: new FormControl(), //Default to earliest set date
    departurePort: new FormControl<LookupItem>(new LookupItem(''), lookupItemValidator()),
    isEMTrip: new FormControl<boolean>(false) //Must select before starting trip???
  });

  hmsPortOptions: LookupItem[] = [];
  trawlPortOptions: LookupItem[] = [];
  filteredDeparturePorts!: Observable<LookupItem[]>;
  portSubscription!: Subscription;
  formUpdateSubscription!: Subscription;
  private trawlDepartureDate: Date | undefined;

  constructor(
    public dataSync: LocalStorageService,
    public tripValidator: TripValidator,
    public router: Router,
    public lookupService: LookupDataService,
    public route: ActivatedRoute,
    public dialog: MatDialog,
    private auth: AuthService,
    private toastr: ToastrService,
    private insights: Insights,
    private onlineStatusService: OnlineStatusService) {
      let fromFishery = this.route.snapshot.queryParams.fishery ?? 0;
      this.selectedFishery = linkedSignal(() => 
        dataSync.current_user().groundfishFishery && dataSync.current_user().hmsFishery ? fromFishery :
        dataSync.current_user().groundfishFishery ? Fishery.Groundfish : 
        dataSync.current_user().hmsFishery ? Fishery.HMS : 
        dataSync.current_user().trawlFishery ? Fishery.Trawl : Fishery.Groundfish);

        this.hmsPortOptions = this.lookupService.getHmsPorts();
        this.portSubscription = this.lookupService.getPortsObservable().subscribe((value) => this.trawlPortOptions = value);

        this.editedDeliveryId = computed(() => dataSync.edited_logbook().GF.logbookId ?? dataSync.edited_logbook().HMS.logbookId);
        this.editedTrawlDeliveryId = computed(() => dataSync.edited_trawlLogbook().logbookId);
        this.hasDraftTrips = computed(() => dataSync.hms_drafts().length > 0 || dataSync.trawl_drafts().length > 0);

        this.filteredDeparturePorts = this.landingForm.get('departurePort')!.valueChanges.pipe(
          startWith(''),
          map(value => (this.selectedFishery() == Fishery.HMS ? this.hmsPortOptions : this.trawlPortOptions).filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
        );

        this.groundfishSets = computed(() => dataSync.user_trip().sets.filter(set => set.fishery == "Groundfish"));
        this.insights.trackEvent('MenuDataInit', 'On menu with ' + this.groundfishSets().length + ' sets');
        this.hmsSets = computed(() => 
                                    dataSync.user_trip().sets.filter(set => set.fishery == "HMS").sort((a, b) =>
                                  (a.startDate ? new Date(a.startDate).getTime() : Number.MIN_SAFE_INTEGER) -
                                  (b.startDate ? new Date(b.startDate).getTime() : Number.MIN_SAFE_INTEGER)));

        this.trawlSets = computed(() => dataSync.user_trawlTrip().sets);
        this.panelOpen = linkedSignal(() =>
          this.selectedFishery() === Fishery.Trawl ? true : untracked(() => dataSync.user_trip().hmsDelivery.isNewDelivery)
        );
        this.setErrors = computed(() => dataSync.current_user().trawlFishery ? this.tripValidator.trawlErrorWarningDialogContent() :
                                        this.tripValidator.errorWarningDialogContent(dataSync.user_trip(), dataSync.current_user()));

        if (this.selectedFishery() == Fishery.HMS) {
          effect(() => {
            const hmsDelivery = dataSync.user_trip().hmsDelivery;
            this.landingForm.patchValue({
              departureDate: hmsDelivery.departureDate ? formatDate(hmsDelivery.departureDate, 'fullDate', 'en-US') : '',
              departurePort: hmsDelivery.departurePort.name ? hmsDelivery.departurePort : ''
            }, { emitEvent: false });
          });
        } else if (this.selectedFishery() == Fishery.Trawl) {
          effect(() => {
            const trawlDelivery = dataSync.user_trawlTrip().trawlDelivery;
            const d = trawlDelivery.departureDate;
            if (d instanceof Date && !isNaN(d.getTime())) this.trawlDepartureDate = d;
            this.landingForm.patchValue({
              departureDate: d ? formatDate(d, 'EEEE, MMM d, y, HH:mm', 'en-US') : '',
              departurePort: trawlDelivery.departurePort.name ? trawlDelivery.departurePort : '',
              isEMTrip: trawlDelivery.isEMTrip
            }, { emitEvent: false });
          });
        }
        
  }

  ngOnInit(): void {

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Scroll to top on navigation end
        window.scrollTo(0, 0);
      }
    });

    this.formUpdateSubscription = this.landingForm.valueChanges.subscribe(value => {
      if (this.selectedFishery() == Fishery.HMS) {
        this.dataSync.user_trip.update(trip => {
          trip.hmsDelivery.departureDate = value.departureDate;
          trip.hmsDelivery.departurePort = value.departurePort;
          return trip;
        });

        this.dataSync.updateLanding(Fishery.HMS, false).then(res => {
          if (!res.success) {
            this.toastr.error("Error updating delivery", "Error");
            this.insights.trackException(res.error);
          }
        });

      } else if (this.selectedFishery() == Fishery.Trawl) {
        this.dataSync.user_trawlTrip.update(trawlTrip => {
          if (this.trawlDepartureDate) trawlTrip.trawlDelivery.departureDate = this.trawlDepartureDate;
          trawlTrip.trawlDelivery.departurePort = value.departurePort;
          trawlTrip.trawlDelivery.isEMTrip = value.isEMTrip;
          return trawlTrip;
        });
        this.dataSync.updateLanding(Fishery.Trawl, false, 'trawl').then(res => {
          if (!res.success) {
            this.toastr.error("Error updating delivery", "Error");
            this.insights.trackException(res.error);
          }
        })
      }
      console.log("Update landing")    
    });
  }

  editDateTime() {
    const isTrawl = this.selectedFishery() == Fishery.Trawl;
    let dateInput: Date;
    if (isTrawl) {
      const stored = this.dataSync.user_trawlTrip().trawlDelivery.departureDate;
      dateInput = (stored instanceof Date && !isNaN(stored.getTime())) ? stored : new Date();
    } else {
      dateInput = this.landingForm.get('departureDate')?.value == '' ? new Date() : new Date(this.landingForm.get('departureDate')?.value);
    }
    let minArrivalDate = this.selectedFishery() == Fishery.HMS ? this.hmsSets().map(s => new Date(s.startDate)).filter(d => d && !Number.isNaN(d.getTime()))
    .reduce((curr, acc) => new Date(curr).toISOString() > new Date(acc).toISOString() ? curr : acc, dateInput)
    :
    this.dataSync.user_trawlTrip().trawlDelivery.returnDate ?? new Date();

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      data: { date: dateInput, minDate: minArrivalDate, isDelivery: true, isTrawl: isTrawl, hmsField: 'departureDate' },
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        let selectedDate = new Date(result);
        if (isTrawl) this.trawlDepartureDate = selectedDate;
        this.landingForm.get('departureDate')?.setValue(formatDate(selectedDate, isTrawl ? 'EEEE, MMM d, y, HH:mm' : 'fullDate', 'en-US'));
      }
    });
  }

  /**
  * Navigates the user to the set for a given row in the table when clicked
  */
  clickRow(set: any) {
    if (set instanceof TrawlSet) {
      let ind = this.dataSync.user_trawlTrip().sets.findIndex(s => s.fishingEventID == set.fishingEventID);
      this.router.navigate(['set_detail_trawl', ind, false]);
    }
    else {
      let ind = this.dataSync.user_trip().sets.findIndex(s => s.fishingEventID == set.fishingEventID);
      if (ind == -1) this.insights.trackEvent('Missing set', 'Could not find set. ' + this.dataSync.user_trip().sets.length + ' sets found');
      if (set.fishery == "Groundfish") this.router.navigate(['set_detail', ind, false]);
      else if (set.fishery == "HMS") this.router.navigate(['set_detail_hms', ind, false]);
      else console.log("Error: Fishery type not found");
    }
  }


  /**
* Handles whether the set menu should open when add set button is clicked
*/
  handleAddSetClick() {
    const user = this.dataSync.current_user();
    if (user.groundfishFishery && user.hmsFishery) {
      if (this.selectedFishery() == Fishery.Groundfish) this.addNewSetGroundfish();
      if (this.selectedFishery() == Fishery.HMS) this.addNewSetHMS();
    }
    else if (user.groundfishFishery) this.addNewSetGroundfish();
    else if (user.hmsFishery) this.addNewSetHMS();
    else if (user.trawlFishery) this.addNewSetTrawl();
    else this.addNewSetGroundfish();
  }

  /**
   * Create new set, generate unique id for it, store that id in sets_list, store new set as new document
   */
  addNewSetGroundfish() {
    this.dataSync.storeFishingEvent(new FishingEvent('', "Groundfish"))
    .then(res => {
      let ind = this.dataSync.user_trip().sets.findIndex(s => s.fishingEventID == res.data);
      if (ind == -1) this.insights.trackEvent('Missing set', 'Could not find set. ' + this.dataSync.user_trip().sets.length + ' sets found');
      if (res.success) this.router.navigate(['set_detail', ind, true]);
      else {
        this.toastr.error("Error creating new set", "Error");
        this.insights.trackException(res.error);
      }
  });  
  }


  /**
 * Create new set, generate unique id for it, store that id in sets_list, store new set as new document
 */
  addNewSetHMS() {
    this.dataSync.storeFishingEvent(new FishingEvent('', "HMS"))
    .then(res => {
      if (res.success) this.router.navigate(['set_detail_hms', this.dataSync.user_trip().sets.findIndex(s => s.fishingEventID == res.data), true]);
      else {
        this.toastr.error("Error creating new set", "Error");
        this.insights.trackException(res.error);
      }
    });
  }


  /**
   * Open the in-port dialog (HMS only) and, on confirm, generate one "in port" daily
   * fishing log entry per day in the selected range. Days that already have an HMS set
   * are skipped to avoid duplicates.
   */
  openInPortDialog() {
    const dialogRef = this.dialog.open(HmsInportDialogComponent, {
      width: '500px',
      maxHeight: '90vh',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result: HmsInPortResult | undefined) => {
      if (!result) return;

      const dates = this.datesInRange(result.start, result.end);
      const existingDates = new Set(
        this.hmsSets().filter(s => s.startDate).map(s => new Date(s.startDate).toDateString())
      );
      const newDates = dates.filter(d => !existingDates.has(d.toDateString()));
      const skipped = dates.length - newDates.length;

      if (newDates.length === 0) {
        this.toastr.info('All selected dates already have a daily fishing log entry', 'Nothing to add');
        return;
      }

      const events = newDates.map(d => this.buildInPortEvent(d, result));
      this.dataSync.storeHmsInPortSets(events).then(res => {
        if (res.success) {
          this.toastr.success(
            `Created ${newDates.length} in-port day${newDates.length === 1 ? '' : 's'}` +
              (skipped > 0 ? ` (skipped ${skipped} that already had entries)` : ''),
            'In-Port Days Added'
          );
        } else {
          this.toastr.error('Error creating in-port days', 'Error');
          this.insights.trackException(res.error);
        }
      });
    });
  }


  /**
   * Build the list of calendar days (at local midnight) from start to end, inclusive.
   */
  private datesInRange(start: Date, end: Date): Date[] {
    const dates: Date[] = [];
    const current = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    while (current.getTime() <= last.getTime()) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }


  /**
   * Construct a single in-port HMS fishing event for the given date using the shared
   * gear info from the dialog. Fishing mode is "Port" and hours fished is 0.
   */
  private buildInPortEvent(date: Date, result: HmsInPortResult): FishingEvent {
    const event = new FishingEvent('', 'HMS');
    event.startDate = date;
    event.fishingMode = 'Port';
    event.hoursFished = 0;

    const gear = new Gear();
    gear.fishery = 'HMS';
    gear.type = result.gearType;
    gear.gearSubtype = result.gearSubtype ?? new LookupItem('');
    event.gear = gear;

    if (result.gearType === 'Deep Set Buoy Gear') {
      event.buoyCount = result.buoyCount ?? 0;
      event.hookCount = result.hookCount ?? 0;
    }
    if (result.gearType === 'Harpoon') event.aircraftUsed = result.aircraftUsed ?? false;

    // Clone the coordinates so each generated set owns an independent copy
    event.startLat = Coordinate.transformAnon(result.lat);
    event.startLon = Coordinate.transformAnon(result.lon);
    event.startLatManualEntry = !event.startLat.isUnfilled();
    event.startLonManualEntry = !event.startLon.isUnfilled();
    event.startPositionAccuracy = result.positionAccuracy ?? undefined;

    return event;
  }

    /**
 * Create new set, generate unique id for it, store that id in sets_list, store new set as new document
 */
  addNewSetTrawl() {
    this.dataSync.storeFishingEvent(new TrawlSet(), "trawl")
    .then(res => { 
      if (res.success) this.router.navigate(['set_detail_trawl', this.dataSync.user_trawlTrip().sets.findIndex(s => s.fishingEventID == res.data), true]);
      else {
        this.toastr.error("Error creating new set", "Error");
        this.insights.trackException(res.error);
      }
  });
  }


  /**
* Handles whether the menu should open when delivery button is clicked
*/
  handleDeliveryClick() {
    const user = this.dataSync.current_user();
    if (user.groundfishFishery && user.hmsFishery) {
      if (this.selectedFishery() == Fishery.Groundfish) this.goToDeliveryGroundfish();
      if (this.selectedFishery() == Fishery.HMS) this.goToDeliveryHMS();
    }
    else if (user.hmsFishery) this.goToDeliveryHMS();
    else if (user.groundfishFishery) this.goToDeliveryGroundfish();
    else if (user.trawlFishery) this.goToDeliveryTrawl();
  }


  /**
* Go to groundfish delivery
*/
  goToDeliveryGroundfish() {
    this.router.navigate(['delivery_detail', 0, 0]);
  }

/**
* Go to HMS delivery
*/
  goToDeliveryHMS() {
    this.router.navigate(['hms_delivery_detail', 0, 0])
  }


/**
* Go to Trawl delivery
*/
  goToDeliveryTrawl() {
    this.router.navigate(['trawl_delivery', 0, 0]);
  }


/**
* Go to HMS or Trawl draft page
*/
  handleDraftClick() {
    if (this.dataSync.current_user().hmsFishery) this.router.navigate(['hms_drafts']);
    else if (this.dataSync.current_user().trawlFishery) this.router.navigate(['trawl_drafts']);
  }


  /**
  * Get errors from {@link TripValidator.errorWarningDialogContent}
  */
  generateSetErrors() {
    let h = (Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0) - 10).toString() + 'px';
    let w = (Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) - 10).toString() + 'px';
    //Launch dialog with collected results
    const dialogRef = this.dialog.open(ErrorWarningDialogComponent, {
      autoFocus: false,
      width: w,
      height: h,
      data: { errors: this.setErrors(), submission: false },
    });

    //Clicking on a block will probably have to take user to clicked set
    dialogRef.afterClosed().subscribe(result => {
      if (result != undefined) {
        if (this.selectedFishery() == Fishery.Trawl) {
          if (result == -1) this.router.navigate(['trawl_delivery', 0, 0]);
          else {
            // result is fishingEventID, find the index
            const setIndex = this.trawlSets().findIndex(s => s.fishingEventID === result);
            this.router.navigate(['set_detail_trawl', setIndex, false]);
          }
        } else {
          if (result == -1) this.router.navigate(['delivery_detail', 0, 0]);
          else {
            // result is fishingEventID, find the set and its index
            const set = this.dataSync.user_trip().sets.find(s => s.fishingEventID === result);
            const setIndex = this.dataSync.user_trip().sets.findIndex(s => s.fishingEventID === result);
            if (set?.fishery === "Groundfish") this.router.navigate(['set_detail', setIndex, false]);
            else if (set?.fishery === "HMS") this.router.navigate(['set_detail_hms', setIndex, false]);
          }
        }
      }
    });
  }
  /**
* Returns the length of the setErrors array for the current tripIndex.
*/
  get SetErrorsLength(): number {
    return this.setErrors().length ?? 0;
  }

  get AnyErrors(): boolean {
    return this.setErrors().some(se => se.isError);
  };

  /**
   * Set the login_status document, cleanup storage, unset {@link LocalStorageService.current_user}
   */
  logout() {
    this.dataSync.sync_in_progress.set(false);
    this.dataSync.logged_in.set(false);
    this.dataSync.local_storage.upsert('login_status', (doc: any) => {
          doc.user = '';
          doc.isLoggedIn = false;
          return doc;
    });
    this.dataSync.cleanupStorage();
         
    if (this.auth.checkAccessToken().expired || this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE)
      this.router.navigate(['login']);
    else if (this.onlineStatusService.getStatus() == OnlineStatusType.ONLINE)
      this.auth.postRevokeToken().subscribe(_ => this.router.navigate(['login']));
         
    this.dataSync.current_user.set(new OperatorUser);
  }


  ngOnDestroy(): void {
    if (this.portSubscription) this.portSubscription.unsubscribe();
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
  }
}